import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChurchEvent } from '../../types';
import { events as eventsApi, eventList } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { formatDate, formatDateTime, formatCurrency } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, DateRangeFilter, DataTable, Pagination, Notification,
  FormLayout, Input, Select, SearchableSelect, Badge, Button, Drawer, RowActions,
  ConfirmDialog, PromptDialog, PrintButton
} from '../../components/ui';
import type { Column } from '../../components/ui';
import EventSummary from './EventSummary';

const CATEGORIES = ['Worship', 'Youth', 'Choir', 'Sunday School', 'Outreach', 'Committee', 'Fellowship', 'Special'];
const LIFECYCLE = ['Upcoming', 'Completed', 'Cancelled'];
const RECURRENCE = ['None', 'Weekly', 'Monthly'];

type EventForm = Pick<ChurchEvent,
  'eventName' | 'category' | 'eventDate' | 'time' | 'endDate' | 'endTime' | 'venue' |
  'organiser' | 'organiserContact' | 'budget' | 'participants' |
  'actualParticipants' | 'actualCost' | 'description' | 'recurrence' | 'eventStatus'
> & { eventId?: string; repeatUntil?: string };

const EMPTY_FORM: EventForm = {
  eventName: '', category: '', eventDate: '', time: '', endDate: '', endTime: '', venue: '',
  organiser: '', organiserContact: '', budget: '', participants: '',
  actualParticipants: '', actualCost: '', description: '', recurrence: 'None', eventStatus: '',
  repeatUntil: ''
};

const today = () => new Date().toISOString().slice(0, 10);
const stamp = () => new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** The event's lifecycle: an explicit choice wins; otherwise date decides. */
const effectiveStatus = (ev: ChurchEvent): string =>
  ev.eventStatus || (String(ev.eventDate || '') && String(ev.eventDate) < today() ? 'Completed' : 'Upcoming');

const statusTone = (status: string) =>
  status === 'Completed' ? 'success' : status === 'Cancelled' ? 'error' : 'accent';

/** Step a YYYY-MM-DD date forward, for generating a recurring series. */
const stepDate = (iso: string, weeks: number, months: number): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (weeks) d.setDate(d.getDate() + weeks * 7);
  if (months) d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const EventManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // The Summary opens first; the calendar is one tab away.
  const [view, setView] = useState('summary');
  const [formData, setFormData] = useState<EventForm>(EMPTY_FORM);
  const [editingRecord, setEditingRecord] = useState<ChurchEvent | null>(null);
  const [viewingEvent, setViewingEvent] = useState<ChurchEvent | null>(null);
  const [previewEvent, setPreviewEvent] = useState<ChurchEvent | null>(null);
  const [pendingCancel, setPendingCancel] = useState<ChurchEvent | null>(null);
  const [pendingVoid, setPendingVoid] = useState<ChurchEvent | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Set when a new event clashes with one already on the same date + time; the
  // confirm dialog saves it anyway or backs out (CHM-058).
  const [pendingDuplicate, setPendingDuplicate] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { notification, notify, dismiss } = useNotification();

  const isDeletedView = view === 'deleted';
  const isListView = view === 'directory' || isDeletedView;
  const bucket = isDeletedView ? 'deleted' : 'active';

  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => { setPageNum(1); }, [view, debouncedSearch, categoryFilter, statusFilter, startDate, endDate]);

  const { data: rows, raw: pageRaw, loading, error, refetch: refetchPage } =
    useFetchList<ChurchEvent>((p, o) => eventList.page(p, o), {
      params: { bucket, q: debouncedSearch, category: categoryFilter, status: statusFilter, from: startDate, to: endDate, page: pageNum - 1, size: PAGE_SIZE },
      enabled: isListView
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const [counts, setCounts] = useState({ active: 0, deleted: 0 });
  const refetchCounts = useCallback(async () => {
    try {
      const c = await eventList.counts();
      setCounts({ active: Number(c.active) || 0, deleted: Number(c.deleted) || 0 });
    } catch { /* keep last */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  const [categoryOptions, setCategoryOptions] = useState<string[]>(CATEGORIES);
  // Fetched (not fixed) so categories that only exist in the data show in the
  // filter. Refetched after every save/delete via `refetch`, so a category
  // introduced by a new event appears without a page reload (CHM-015).
  const refetchCategories = useCallback(async () => {
    try {
      const list = await eventList.categories();
      setCategoryOptions(Array.from(new Set([...CATEGORIES, ...(list || [])])).sort());
    } catch { /* keep defaults */ }
  }, []);
  useEffect(() => { refetchCategories(); }, [refetchCategories]);

  // The Summary works over every active event: the calendar's range endpoint
  // with an open-ended range returns them all (voided ones are left out).
  const [allEvents, setAllEvents] = useState<ChurchEvent[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const refetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try { setAllEvents(getList<ChurchEvent>(await eventList.range({ from: '0000-01-01', to: '9999-12-31' }))); }
    catch { setAllEvents([]); }
    finally { setSummaryLoading(false); }
  }, []);
  useEffect(() => { if (view === 'summary') refetchSummary(); }, [view, refetchSummary]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts(), refetchSummary(), refetchCategories()]);
  }, [refetchPage, refetchCounts, refetchSummary, refetchCategories]);

  const page = {
    currentItems: rows, currentPage: pageNum, totalPages, totalItems, itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1, isLast: pageNum >= totalPages, reset: () => setPageNum(1)
  };

  const clearRange = () => { setStartDate(''); setEndDate(''); };
  const rangeActive = Boolean(startDate || endDate);

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingRecord(null); setErrors({}); };
  const goToDirectory = () => { resetForm(); setView('directory'); setSearchTerm(''); setPageNum(1); };
  const startCreate = () => { resetForm(); setView('registration'); };

  const startEdit = (event: ChurchEvent) => {
    setEditingRecord(event);
    setFormData({ ...EMPTY_FORM, ...event, recurrence: 'None', repeatUntil: '' });
    setErrors({});
    setViewingEvent(null);
    setView('registration');
  };

  // Arrived from the Calendar's "Edit this event" (CHM-059): open that event's
  // form straight away, then drop the hand-over so a refresh shows the list.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const handed = (location.state as { editEvent?: ChurchEvent } | null)?.editEvent;
    if (handed && handed.id != null) {
      startEdit(handed);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Open the record" from the Approvals inbox: the directory (or Deleted
  // History), then the event's details.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, { event: eventsApi.get });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const ev = found.record as ChurchEvent;
    resetForm();
    setView(ev.status === 'VOID' ? 'deleted' : 'directory');
    setSearchTerm(''); setPageNum(1);
    setViewingEvent(ev);
  });

  const setField = (name: keyof EventForm) => (e) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found: Record<string, string> = {};
    if (!formData.eventName?.trim()) found.eventName = 'Choose the event name.';
    if (!formData.eventDate?.trim()) found.eventDate = 'The event date is required.';
    if (!formData.time?.trim()) found.time = 'The start time is required.';
    if (!formData.venue?.trim()) found.venue = 'The venue is required.';
    if (!formData.organiser?.trim()) found.organiser = 'The organiser is required.';
    if (formData.endDate && formData.eventDate && formData.endDate < formData.eventDate) {
      found.endDate = 'The end date cannot be before the start date.';
    }
    if (Object.keys(found).length) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    // A new event on a date + start time already taken warns first, then saves
    // on confirm — the same courtesy Attendance gives its records (CHM-058).
    if (!editingRecord) {
      const sameDay = await eventList
        .page({ bucket, from: formData.eventDate, to: formData.eventDate, page: 0, size: 100 })
        .catch(() => null);
      const clash = getList<ChurchEvent>(sameDay).some((ev) => (ev.time || '') === (formData.time || ''));
      if (clash) { setPendingDuplicate(true); return; }
    }

    await doSave();
  };

  const doSave = async () => {
    const { repeatUntil, recurrence, ...fields } = formData;
    setSaving(true);
    try {
      if (editingRecord) {
        await eventsApi.update(editingRecord.id, { ...editingRecord, ...fields });
        await refetch();
        goToDirectory();
        notify('Event updated.', 'success');
        return;
      }

      // New event(s). A recurrence builds a series of independent events; the
      // backend gives each its own Event ID.
      const dates = [fields.eventDate as string];
      if ((recurrence === 'Weekly' || recurrence === 'Monthly') && repeatUntil) {
        let cur = fields.eventDate as string;
        for (let i = 0; i < 104; i += 1) {
          cur = stepDate(cur, recurrence === 'Weekly' ? 1 : 0, recurrence === 'Monthly' ? 1 : 0);
          if (cur > repeatUntil) break;
          dates.push(cur);
        }
      }

      await Promise.all(dates.map((d) => eventsApi.create({ ...fields, eventDate: d, status: 'ACTIVE' } as Partial<ChurchEvent>)));
      await refetch();
      goToDirectory();
      notify(dates.length > 1 ? `Scheduled ${dates.length} events (${recurrence.toLowerCase()}).` : 'Event scheduled.', 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the event.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const doCancel = async (event: ChurchEvent) => {
    setPendingCancel(null);
    try {
      await eventsApi.update(event.id, { ...event, eventStatus: 'Cancelled' } as Partial<ChurchEvent>);
      await refetch();
      notify(`${event.eventName} marked as Cancelled.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not cancel the event.', 'error');
    }
  };

  const confirmVoid = async (reason: string) => {
    const event = pendingVoid;
    setPendingVoid(null);
    if (!event) return false;
    try {
      await eventsApi.update(event.id, { ...event, status: 'VOID', deleteReason: reason, deletedAt: stamp() } as Partial<ChurchEvent>);
      await refetch();
      notify('Event deleted. Kept in Deleted History.', 'success');
      return true;
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the event.', 'error');
      return false;
    }
  };

  const handleRestore = async (event: ChurchEvent) => {
    try {
      await eventsApi.update(event.id, { ...event, status: 'ACTIVE', deleteReason: null, deletedAt: null } as Partial<ChurchEvent>);
      await refetch();
      notify(`Event ${event.eventId} restored.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not restore the event.', 'error');
    }
  };


  const baseColumns: Array<Column<ChurchEvent>> = [
    { key: 'eventId', header: 'Event ID', nowrap: true, render: (r) => <strong>{r.eventId}</strong> },
    { key: 'eventName', header: 'Event Name' },
    { key: 'category', header: 'Category', render: (r) => (r.category ? <Badge tone="neutral">{r.category}</Badge> : '-') },
    {
      key: 'when', header: 'Date & Time', nowrap: true,
      render: (r) => (
        <span>
          {formatDateTime(r.eventDate, r.time)}
          {r.endDate && r.endDate !== r.eventDate && <span style={{ color: t.textFaint }}> → {formatDate(r.endDate)}</span>}
        </span>
      )
    },
    { key: 'venue', header: 'Venue' },
    { key: 'status', header: 'Status', render: (r) => { const st = effectiveStatus(r); return <Badge tone={statusTone(st)}>{st}</Badge>; } }
  ];

  // The row menu is a screen control; the printed schedule drops that column
  // rather than carrying an empty one down the page.
  const [printing, setPrinting] = useState(false);

  const actionsColumn: Column<ChurchEvent> = {
    key: 'actions', header: 'Actions', align: 'right', nowrap: true,
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <RowActions
          isDarkMode={isDarkMode}
          items={isDeletedView
            ? [{ icon: '♻️', label: 'Restore', onClick: () => handleRestore(r) }]
            : [
                { icon: '👁️', label: 'View', onClick: () => setViewingEvent(r) },
                // A finished event is a record of what happened; editing it would
                // rewrite history, so Edit is disabled once it is Completed (CHM-016).
                { icon: '✏️', label: 'Edit', write: true, disabled: effectiveStatus(r) === 'Completed', onClick: () => startEdit(r) },
                ...(effectiveStatus(r) === 'Cancelled'
                  ? []
                  : [{ icon: '🚫', label: 'Cancel Event', onClick: () => setPendingCancel(r) }]),
                { icon: '🗑️', label: 'Delete', separated: true, danger: true, onClick: () => setPendingVoid(r) }
              ]}
        />
      </span>
    )
  };

  const columns: Array<Column<ChurchEvent>> = isDeletedView
    ? [
        ...baseColumns,
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        actionsColumn
      ]
    : [...baseColumns, actionsColumn];

  // --- Full-screen single-event print sheet ---
  if (previewEvent) {
    const r = previewEvent;
    const row = (label: string, value) => (
      <tr>
        <td style={{ padding: '8px 12px', fontWeight: 'bold', width: '40%', borderBottom: '1px solid #ddd' }}>{label}</td>
        <td style={{ padding: '8px 12px', borderBottom: '1px solid #ddd' }}>{value || '-'}</td>
      </tr>
    );
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '20px' }}>
          <Button variant="secondary" onClick={() => setPreviewEvent(null)}>← Back to List</Button>
          <div style={{ display: 'flex', gap: '10px' }}>
            {effectiveStatus(r) !== 'Completed' && (
              <Button variant="ghost" onClick={() => { const ev = r; setPreviewEvent(null); startEdit(ev); }}>✏️ Edit</Button>
            )}
          </div>
        </div>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <tbody>
              {row('Event ID', r.eventId)}
              {row('Event Name', r.eventName)}
              {row('Category', r.category)}
              {row('Status', effectiveStatus(r))}
              {row('Date', formatDateTime(r.eventDate, r.time))}
              {row('Ends', r.endDate ? formatDateTime(r.endDate, r.endTime) : '')}
              {row('Venue', r.venue)}
              {row('Organiser', r.organiser)}
              {row('Contact', r.organiserContact)}
              {row('Expected Participants', r.participants)}
              {row('Actual Participants', r.actualParticipants)}
              {row('Allocated Budget', formatCurrency(r.budget))}
              {row('Actual Cost', formatCurrency(r.actualCost))}
              {row('Notes', r.description)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="⛪"
        title="Event Management"
        activeTab={view}
        onTabChange={(key) => {
          if (key === 'registration') startCreate();
          else if (key === 'summary') { resetForm(); setView('summary'); }
          else if (key === 'deleted') { resetForm(); setView('deleted'); setSearchTerm(''); setPageNum(1); }
          else goToDirectory();
        }}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'directory', label: `Event Calendar (${counts.active})` },
          { key: 'deleted', label: `🗑️ Deleted History (${counts.deleted})`, variant: 'danger' },
          { key: 'registration', write: true, label: editingRecord ? '✏️ Editing…' : '+ Add New Entry' }
        ]}
      />

      {isListView && (
        <>
          {/* One row, as on Members & Families: a compact search field, the
              date range / category / status filters beside it, and the
              export/print buttons at the far right. Wraps on a narrow screen. */}
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm('')}
            placeholder="Search by ID, name, organiser, venue or category..."
            fieldStyle={{ flex: '0 1 300px', minWidth: '200px' }}
            style={{ flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}
            actions={
              <>
                <PrintButton
                  module="Event Management"
                  documentType="EVENT_SCHEDULE"
                  size="sm"
                  title="Event-Schedule"
                  data={() => ({
                    title: 'Event Schedule',
                    reference: isDeletedView ? 'Deleted events' : 'Active events',
                    rowCount: page.currentItems.length,
                    // The module's own records, untouched: a template names the
                    // fields this register actually stores.
                    rows: page.currentItems
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                <DateRangeFilter
                  startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
                  clear={clearRange} isActive={rangeActive} onChange={() => setPageNum(1)} isDarkMode={isDarkMode}
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Category:</span>
                  <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                    options={categoryOptions} placeholder="All" containerStyle={{ margin: 0 }} style={{ margin: 0, width: 'auto' }} isDarkMode={isDarkMode} />
                </div>
                {!isDeletedView && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Status:</span>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                      options={LIFECYCLE} placeholder="All" containerStyle={{ margin: 0 }} style={{ margin: 0, width: 'auto' }} isDarkMode={isDarkMode} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                </div>
              </>
            }
          />

          <div>
            {totalPages > 1 && (
              <p style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '12px' }}>
                Page {pageNum} of {totalPages} — {totalItems} events in all
              </p>
            )}
            <DataTable
              columns={printing ? columns.filter((c) => c.key !== 'actions') : columns}
              rows={page.currentItems}
              loading={loading}
              onRowClick={(r) => (isDeletedView ? undefined : setViewingEvent(r))}
              emptyMessage={error ? 'Could not load events.' : 'No events scheduled.'}
            />
          </div>

          <div>
            <Pagination {...page} itemsOnPage={page.currentItems.length} label="events" />
          </div>
        </>
      )}

      {view === 'summary' && (
        <EventSummary events={allEvents} loading={summaryLoading} isDarkMode={isDarkMode} />
      )}

      {view === 'registration' && (
        <FormLayout
          title={editingRecord ? 'Edit Event Details' : 'Schedule New Event'}
          submitLabel={editingRecord ? 'Update Event' : 'Save & Schedule Event'}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={editingRecord ? goToDirectory : undefined}
          maxWidth="860px"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Event Name" required
              value={formData.eventName}
              onChange={(v) => { setFormData((prev) => ({ ...prev, eventName: v })); setErrors((e) => ({ ...e, eventName: undefined })); }}
              storageKey="church_event_types"  error={errors.eventName}
            />
            <Select label="Category / Ministry" value={formData.category || ''} onChange={setField('category')}
              options={CATEGORIES} placeholder="Select…" isDarkMode={isDarkMode} />
            <Input label="Start Date" name="eventDate" type="date" required value={formData.eventDate} error={errors.eventDate} onChange={setField('eventDate')} />
            <Input label="Start Time" name="time" type="time" required value={formData.time} error={errors.time} onChange={setField('time')} />
            <Input label="End Date (multi-day)" name="endDate" type="date" value={formData.endDate} error={errors.endDate} onChange={setField('endDate')} />
            <Input label="End Time" name="endTime" type="time" value={formData.endTime} onChange={setField('endTime')} />
            <Input label="Venue / Location" name="venue" required value={formData.venue} error={errors.venue} onChange={setField('venue')} />
            <Input label="Organiser" name="organiser" required value={formData.organiser} error={errors.organiser} onChange={setField('organiser')} />
            <Input label="Organiser Contact" name="organiserContact" placeholder="Phone / email" value={formData.organiserContact} onChange={setField('organiserContact')} />
            {editingRecord && (
              <Select label="Status" value={formData.eventStatus || ''} onChange={setField('eventStatus')}
                options={LIFECYCLE} placeholder="Auto (by date)" isDarkMode={isDarkMode} />
            )}
          </FormLayout.Grid>

          <FormLayout.Section title="Budget & Participants" />
          <FormLayout.Grid>
            <Input label="Allocated Budget (₹)" name="budget" type="number" value={formData.budget} onChange={setField('budget')} />
            <Input label="Expected Participants" name="participants" type="number" value={formData.participants} onChange={setField('participants')} />
            {editingRecord && (
              <>
                <Input label="Actual Cost (₹)" name="actualCost" type="number" value={formData.actualCost} onChange={setField('actualCost')} />
                <Input label="Actual Participants" name="actualParticipants" type="number" value={formData.actualParticipants} onChange={setField('actualParticipants')} />
              </>
            )}
          </FormLayout.Grid>

          {!editingRecord && (
            <>
              <FormLayout.Section title="Repeat (optional)" />
              <FormLayout.Grid>
                <Select label="Repeats" value={formData.recurrence || 'None'} onChange={setField('recurrence')}
                  options={RECURRENCE} placeholder="" isDarkMode={isDarkMode} />
                {formData.recurrence && formData.recurrence !== 'None' && (
                  <Input label="Repeat Until" name="repeatUntil" type="date"
                    value={formData.repeatUntil} onChange={setField('repeatUntil')}
                    hint="One event is created for each date up to here." />
                )}
              </FormLayout.Grid>
            </>
          )}

          <FormLayout.Grid>
            <Input label="Description / Notes" name="description" type="textarea" fullWidth rows={3} maxLength={500}
              placeholder="Agenda, special arrangements, remarks…"
              value={formData.description} onChange={setField('description')} />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewingEvent && (
        <Drawer
          open
          title={viewingEvent.eventName}
          subtitle={`${viewingEvent.eventId} · ${effectiveStatus(viewingEvent)}`}
          onClose={() => setViewingEvent(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button write variant="secondary" onClick={() => startEdit(viewingEvent)}>✏️ Edit</Button>
            </div>
          }
        >
          <Drawer.Section title="Scheduling Details" isDarkMode={isDarkMode} />
          <Drawer.Row label="Category" value={viewingEvent.category} isDarkMode={isDarkMode} />
          <Drawer.Row label="Date" value={formatDateTime(viewingEvent.eventDate, viewingEvent.time)} isDarkMode={isDarkMode} />
          {viewingEvent.endDate && <Drawer.Row label="Ends" value={formatDateTime(viewingEvent.endDate, viewingEvent.endTime)} isDarkMode={isDarkMode} />}
          <Drawer.Row label="Venue" value={viewingEvent.venue} isDarkMode={isDarkMode} />

          <Drawer.Section title="Management" isDarkMode={isDarkMode} />
          <Drawer.Row label="Organiser" value={viewingEvent.organiser} isDarkMode={isDarkMode} />
          <Drawer.Row label="Contact" value={viewingEvent.organiserContact} isDarkMode={isDarkMode} />
          <Drawer.Row label="Expected Participants" value={viewingEvent.participants} isDarkMode={isDarkMode} />
          <Drawer.Row label="Actual Participants" value={viewingEvent.actualParticipants} isDarkMode={isDarkMode} />
          <Drawer.Row label="Budget" value={formatCurrency(viewingEvent.budget)} isDarkMode={isDarkMode} />
          <Drawer.Row label="Actual Cost" value={viewingEvent.actualCost ? formatCurrency(viewingEvent.actualCost) : '-'} isDarkMode={isDarkMode} />
          {viewingEvent.createdBy && <Drawer.Row label="Created By" value={viewingEvent.createdBy} isDarkMode={isDarkMode} />}

          {viewingEvent.description && (
            <>
              <Drawer.Section title="Notes" isDarkMode={isDarkMode} />
              <Drawer.Row value={viewingEvent.description} isDarkMode={isDarkMode} />
            </>
          )}
        </Drawer>
      )}

      <ConfirmDialog
        open={Boolean(pendingCancel)}
        title="Cancel this event?"
        message={pendingCancel ? `Mark "${pendingCancel.eventName}" as Cancelled? It stays in the calendar, marked cancelled.` : ''}
        confirmLabel="Yes, Cancel Event"
        onConfirm={() => pendingCancel && doCancel(pendingCancel)}
        onCancel={() => setPendingCancel(null)}
      />

      <PromptDialog
        open={Boolean(pendingVoid)}
        title={`Delete Event ${pendingVoid?.eventId || ''}`}
        message="Please enter the reason for deleting this event. It will be kept in Deleted History."
        placeholder="Reason for deletion"
        confirmLabel="Delete Event"
        requiredError="A reason is required to remove an event."
        onSubmit={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />

      <ConfirmDialog
        open={pendingDuplicate}
        title="Possible duplicate"
        message={`An event is already scheduled on ${formData.eventDate ? formatDate(formData.eventDate) : 'this date'}${formData.time ? ` at ${formData.time}` : ''}. Schedule this one anyway?`}
        confirmLabel="Schedule Anyway"
        onConfirm={() => { setPendingDuplicate(false); doSave(); }}
        onCancel={() => setPendingDuplicate(false)}
      />
    </div>
  );
};

export default EventManagement;
